import React, { ReactElement } from "react";
import { Typography } from "../typography/typography";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { CardVariants } from "@/types/global/enum";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/card";
import Video from "../video/Video";
import { Badge } from "@/components/ui/badge";

export enum AlignText {
  left = "text-left",
  right = "text-right",
  center = "text-center",
}

type DisplayCardProps = {
  className?: string;
  images?: string;
  name?: string;
  subtitle?: string;
  price?: string;
  currency?: string;
  condition?: boolean;
  imageLayer?: boolean;
  topImageLayer?: boolean;
  alignText?: AlignText;
  badgeTitle?: string;
  durationText?: string;
  Overlay?: ReactElement;
  variant?: CardVariants;
  videoUrl?: string;
  typographyCSS?: string;
  href?: string;
  CTABottom?: React.ReactNode;
  reserveBottom?: boolean;
};

interface VariantStyles {
  title: string;
  subtitle: string;
  price: string;
}

const variantStyles: Record<CardVariants, VariantStyles> = {
  primary: {
    title: "text-white text-lg font-bold md:text-display-xs",
    subtitle: "text-white text-sm font-medium text-gray-300 pb-1",
    price: "text-white text-lg font-bold md:text-display-xs",
  },
  secondary: {
    title: "text-white text-lg font-bold md:text-display-xs",
    subtitle: "text-white text-md font-medium md:text-xl ",
    price:
      "text-white text-xl font-bold md:text-display-md md:pt-4 leading-text-xl md:leading-display-md",
  },
  tertiary: {
    title: "text-white text-md font-bold md:text-xl",
    subtitle: "text-white text-md font-medium md:text-xl ",
    price: "text-white text-md font-bold md:text-xl",
  },
  quaternary: {
    title: "font-comic-sans text-white text-md font-normal md:text-display-xs",
    subtitle:
      "font-comic-sans text-white text-md font-normal md:font-family-body",
    price:
      "font-comic-sans text-white text-lg font-bold md:text-display-xs md:pt-2",
  },
  quinary: {
    title: "font-comic-sans text-white text-md font-normal md:text-xl",
    subtitle:
      "font-comic-sans text-white text-md font-normal md:font-family-body md:text-lg",
    price:
      "font-comic-sans text-white text-lg font-bold md:text-display-xs md:pt-2",
  },
  senary: {
    title: "text-white font-bold text-sm md:text-md mb-0 md:mb-2",
    subtitle: "text-md text-gray-300 font-medium",
    price: "text-white text-lg font-bold md:text-display-xs",
  },
  septenary: {
    title: "text-white font-bold text-md md:text-display-xs md:mb-2",
    subtitle: "text-xs md:text-sm font-medium text-gray-300 md:mb-2",
    price: "text-white font-bold text-lg md:text-display-xs",
  },

  default: {
    title: "text-white font-medium",
    subtitle: "text-md text-gray-300 font-medium",
    price: "text-white text-xl font-bold",
  },
};

type WrapperLinkProps = {
  href?: string;
  className?: string;
  children: React.ReactNode;
};

export const WrapperLink = ({
  href,
  className,
  children,
}: WrapperLinkProps) => {
  if (!href) {
    return <div className={className}>{children}</div>;
  }

  return (
    <Link href={href} className={className}>
      {children}
    </Link>
  );
};
const DisplayCard = ({
  images,
  name,
  subtitle,
  price,
  currency = "INR",
  condition = false,
  imageLayer = false,
  topImageLayer = false,
  alignText = AlignText.left,
  badgeTitle = "",
  durationText,
  className = "h-[264px] w-[264px]",
  Overlay,
  variant = CardVariants.default,
  videoUrl,
  typographyCSS,
  href,
  CTABottom,
  reserveBottom = false,
}: DisplayCardProps) => (
  <Card
    className={cn(
      `relative overflow-hidden p-0 transition-all hover:shadow-[var(--shadow-card_02)]`,
      className
    )}>
    <CardContent className="relative h-full w-full">
      <div className="absolute inset-0">
        {videoUrl ? (
          <Video videoUrl={videoUrl} />
        ) : (
          images && (
            <Image
              src={images}
              alt={name || "Destination image"}
              fill
              className="object-cover"
            />
          )
        )}
        {topImageLayer && (
          <div className="absolute inset-x-0 top-0 h-1/5 bg-gradient-to-b from-black/90 via-black/60 to-black/0" />
        )}
        {imageLayer && (
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-b from-black/0 via-black/60 to-black/90" />
        )}
        {durationText && (
          <Typography
            as="p"
            className="absolute top-4 left-4 text-sm font-semibold text-gray-100">
            {durationText}
          </Typography>
        )}
        {badgeTitle && (
          <Badge variant="special" className="absolute top-6 left-6">
            {badgeTitle}
          </Badge>
        )}
        {Overlay && (
          <div className="absolute inset-x-0 h-full w-full">{Overlay}</div>
        )}
      </div>

      <div className="pointer-events-none absolute inset-0 flex w-full flex-col items-start justify-end">
        <WrapperLink
          href={href}
          className={cn(
            `flex h-full w-full flex-col justify-end p-4 md:flex`,
            typographyCSS,
            "pointer-events-auto",
            alignText || "justify-end",
            reserveBottom ? "pb-18" : "pb-6"
          )}>
          <div className="flex-1" />
          <Typography as="p" className={cn(variantStyles[variant].title)}>
            {name}
          </Typography>
          <div className="flex flex-col gap-1 md:gap-0">
            <Typography as="p" className={cn(variantStyles[variant].subtitle)}>
              {subtitle}
            </Typography>
            {price && (
              <Typography as="p" className={cn(variantStyles[variant].price)}>
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: currency,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                }).format(parseInt(price, 10))}
                <span className={`${!condition && "hidden"}`}>*</span>
              </Typography>
            )}
          </div>
        </WrapperLink>

        {CTABottom && (
          <div className="pointer-events-auto absolute right-0 bottom-4 left-0 flex cursor-pointer justify-center px-4">
            {CTABottom}
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

export default DisplayCard;
